import { Button } from "@material-ui/core";
import ConfigForm, {
  ConfigInput,
  FormOption,
} from "components/Layout/ConfigForm";
import DottedOutlineButton from "components/Layout/DottedOutlineButton";
import {
  StandardContainer,
  SubmitButtonsContainer,
} from "components/Layout/StandardStyledComponents";
import StepperFrame, {
  InvalidationCheck,
  StepFrame,
} from "components/Layout/StepperFrame";
import React, { Fragment, useState } from "react";
import styled from "styled-components";
import TaskItemCard from "./TaskItemDetails/TaskItemCard";
import TaskItemDetailsForm from "./TaskItemDetailsForm";
import TaskItemTypeForm from "./TaskItemTypeForm";

export enum TaskItemTypeEnum {
  TAKE_PHOTO = "Take Photo",
  CHECKLIST = "Checklist",
  DROPDOWN = "Drop down",
  FORM = "Form",
  BARCODE = "Barcode",
  SIGNATURE = "Signature",
}

export interface TaskItemInput {
  id?: string;
  name: string;
  type: TaskItemTypeEnum;
  options: FormOption[];
}

interface TaskItemListAndAddProps {
  taskItems: TaskItemInput[];
  submitTaskItem: (taskItem: TaskItemInput) => void;
}
interface TaskItemListAndAddState {
  editing: boolean;
  creatingTaskItem: boolean;
  currentTaskItem: TaskItemInput;
}

const DEFAULT_STATE: TaskItemListAndAddState = {
  editing: false,
  creatingTaskItem: false,
  currentTaskItem: {
    name: "",
    type: TaskItemTypeEnum.FORM,
    options: [],
  },
};

export default function TaskItemListAndAdd({
  submitTaskItem,
  taskItems,
}: TaskItemListAndAddProps) {
  const [state, setState] = useState<TaskItemListAndAddState>(DEFAULT_STATE);

  const onOptionAdd = (option: FormOption) => {
    const options = state.currentTaskItem.options;
    options.push(option);
    setState({
      ...state,
      currentTaskItem: { ...state.currentTaskItem, options },
    });
  };

  const onOptionEdit = (option: FormOption) => {};

  const onOptionRemove = (option: FormOption) => {};
  const addTaskItemButton = state.creatingTaskItem ? null : (
    <DottedOutlineButton
      handleClick={() => setState({ ...state, creatingTaskItem: true })}
      title="Add TaskItem"
    />
  );

  const checkIfTaskItemConfigIsInvalid = (): InvalidationCheck => {
    let invalid = false;
    let message = "";
    if (
      state.currentTaskItem.type !== "Form" &&
      state.currentTaskItem.type !== "Take Photo" &&
      state.currentTaskItem.type !== "Checklist" &&
      state.currentTaskItem.type !== "Barcode" &&
      state.currentTaskItem.type !== "Signature" &&
      state.currentTaskItem.type !== "Drop down"
    ) {
      invalid = true;
      message = "Please select type";
    } else {
      invalid = false;
    }
    return { invalid, message };
  };

  const checkIfTaskItemPromptConfigIsInvalid = (): InvalidationCheck => {
    let invalid = false;
    let message = "";
    if (state.currentTaskItem.name === "") {
      invalid = false;
      message = "Please enter prompt for user to see";
    } 
    return { invalid, message };
  };

  const taskItemTypeOptions: FormOption[] = [
    {
      label: TaskItemTypeEnum.TAKE_PHOTO,
      value: TaskItemTypeEnum.TAKE_PHOTO,
    },
    {
      label: TaskItemTypeEnum.BARCODE,
      value: TaskItemTypeEnum.BARCODE,
    },
    {
      label: TaskItemTypeEnum.CHECKLIST,
      value: TaskItemTypeEnum.CHECKLIST,
    },
    {
      label: TaskItemTypeEnum.DROPDOWN,
      value: TaskItemTypeEnum.DROPDOWN,
    },
    {
      label: TaskItemTypeEnum.FORM,
      value: TaskItemTypeEnum.FORM,
    },
    {
      label: TaskItemTypeEnum.SIGNATURE,
      value: TaskItemTypeEnum.SIGNATURE,
    },
  ];
  const stepFrames: StepFrame[] = [
    {
      label: "Choose Type",
      inValid: checkIfTaskItemConfigIsInvalid(),
      children: (
        <TaskItemTypeForm
          taskItemTypeOptions={taskItemTypeOptions}
          type={state.currentTaskItem.type}
          handleTaskItemTypeChange={(event: any) => {
            setState((prevState) => ({
              ...prevState,
              currentTaskItem: {
                ...prevState.currentTaskItem,
                type: event.target.value as TaskItemTypeEnum,
              },
            }));
          }}
        />
      ),
    },
    {
      label: "Configure Task Item",
      inValid: checkIfTaskItemPromptConfigIsInvalid(),
      children: (
        <TaskItemDetailsForm
          type={state.currentTaskItem.type}
          name={state.currentTaskItem.name}
          options={state.currentTaskItem.options}
          onOptionAdd={onOptionAdd}
          onOptionEdit={onOptionEdit}
          onOptionRemove={onOptionRemove}
          handleTaskItemNameChange={(event: any) => {
            setState((prevState) => ({
              ...prevState,
              currentTaskItem: {
                ...prevState.currentTaskItem,
                name: event.target.value,
              },
            }));
          }}
        />
      ),
    },
  ];
  const onCancel = () => {
    setState({
      editing: false,
      creatingTaskItem: false,
      currentTaskItem: {
        name: "",
        type: TaskItemTypeEnum.FORM,
        options: [],
      },
    });
  };

  const onSubmit = () => {
    submitTaskItem(state.currentTaskItem);
    setState({
      editing: false,
      creatingTaskItem: false,
      currentTaskItem: {
        name: "",
        type: TaskItemTypeEnum.FORM,
        options: [],
      },
    });
  };

  const listTaskItems: JSX.Element | null = !state.creatingTaskItem ? (
    <Fragment>
      {taskItems.map((taskItem, index) => {
        return <TaskItemCard key={index} taskItem={taskItem} />;
      })}
    </Fragment>
  ) : null;
  const addTaskItem: JSX.Element | null = state.creatingTaskItem ? (
    <Fragment>
      <StepperFrame
        highlighted={true}
        stepFrames={stepFrames}
        cancellable={true}
        handleCancel={onCancel}
        handleSubmit={onSubmit}
      />
    </Fragment>
  ) : null;
  return (
    <StandardContainer>
      {listTaskItems}
      {addTaskItem}
      {addTaskItemButton}
    </StandardContainer>
  );
}
